import axios from 'axios';
import { API_URL } from '../utils/constants';
import { useAuthStore } from './authStore';

const axiosInstance = axios.create({
  baseURL: API_URL,
});

// ---------------------------------------------------------------------------
// Offline detection
// navigator.onLine is unreliable (returns true on Wi-Fi with no internet).
// We track _knownOffline separately: once a request fails with no server
// response we flip it to true and short-circuit all subsequent requests until
// the browser fires the 'online' event.
// ---------------------------------------------------------------------------
let _knownOffline = !navigator.onLine;

window.addEventListener('online',  () => { _knownOffline = false; });
window.addEventListener('offline', () => { _knownOffline = true; });

export const isDefinitelyOffline = () => _knownOffline || !navigator.onLine;
export const markKnownOffline = () => {
  _knownOffline = true;
};

// REQUEST: abort immediately when offline — no bytes leave the device.
axiosInstance.interceptors.request.use(
  (config) => {
    if (isDefinitelyOffline()) {
      const err = new Error('Network Error');
      err.code = 'ERR_NETWORK_OFFLINE';
      return Promise.reject(err);
    }

    const token = localStorage.getItem('token');
    const activeStoreId = localStorage.getItem('active_store_id');

    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (activeStoreId) {
      config.headers['X-Store-Id'] = activeStoreId;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// RESPONSE: detect expired token; mark offline on any network-level failure.
axiosInstance.interceptors.response.use(
  (response) => response,
  (error) => {
    if (!error?.response) {
      // No server response = network failure; block subsequent requests.
      _knownOffline = true;
    }
    if (error.response?.status === 401) {
      const logout = useAuthStore.getState().logout;
      logout();
    }
    return Promise.reject(error);
  }
);

export default axiosInstance;
